import { AdyenWalletType } from 'types/generatedSchemaTypes';
import {
  SelectedCorporateAccountPaymentOptionStateProps,
  SelectedPaymentCardStateProps,
} from 'types/states';
import { create } from 'zustand';

// Adyen card-add session ids returned by ADYEN_INIT_CARD_ADD. Stashed here at
// flow start so the card-add sheet render sites can read the cardId /
// transactionId. walletType is present only for a stored-wallet add
// (APPLE_PAY/GOOGLE_PAY); absent for a plain card add — the render sites thread
// it into useAdyenCardAdd to pick the right in-sheet component.
export type AdyenCardAddSession = {
  cardId: string;
  transactionId: string;
  walletType?: AdyenWalletType;
};

// The empty/default selected card. Shared so callers that reset the selection
// (e.g. CreditCardForm on add failure) use the exact same shape the store
// initialises with, matching the former reactive-var default.
export const EMPTY_SELECTED_PAYMENT_CARD: SelectedPaymentCardStateProps = {
  cardId: '',
  title: '',
  subtitle: '',
  category: '',
  status: '',
  description: '',
  isDefault: false,
  newCard: false,
  expMonth: '',
  expYear: '',
};

const INITIAL_CORPORATE_ACCOUNT_OPTION: SelectedCorporateAccountPaymentOptionStateProps =
  {
    optionName: '',
    registrationPlates: [],
    validFrom: null,
    validTo: null,
    method: 'CORPORATE_ACCOUNT',
  };

type PaymentState = {
  selectedPaymentCard: SelectedPaymentCardStateProps;
  selectedCorporateAccountPaymentOption: SelectedCorporateAccountPaymentOptionStateProps;
  creditCardCreationTerminalUrl: string | undefined;
  // In-memory only (matches the former reactive var). Redirect survival is
  // handled separately by utils/adyenRedirectSession, which mirrors the session
  // into web sessionStorage — this store field is expected to be gone after a
  // full redirect round-trip and must NOT be persisted.
  adyenCardAddSession: AdyenCardAddSession | undefined;
  // Client-side one-per-type wallet slot after a successful wallet init, keyed
  // by occupied-at epoch ms. Bridges the gap until GET_CUSTOMER cache catches
  // up (or a refresh fails). No age-based expiry: the card-add flow clears it
  // on refused/cancelled/error/abandon, and removal/logout clear it too.
  adyenWalletSlotOccupiedAt: Partial<Record<AdyenWalletType, number>>;
  externalPaymentFlowLoading: boolean;
  externalPaymentFlowCompleted: boolean;
  // Parking session waiting for its Swish payment, so the app can check the
  // result when the customer returns.
  externalPaymentSessionId: string | null;
};

type PaymentActions = {
  setSelectedPaymentCard: (card: SelectedPaymentCardStateProps) => void;
  resetSelectedPaymentCard: () => void;
  setSelectedCorporateAccountPaymentOption: (
    option: SelectedCorporateAccountPaymentOptionStateProps
  ) => void;
  setCreditCardCreationTerminalUrl: (url: string | undefined) => void;
  setAdyenCardAddSession: (session: AdyenCardAddSession | undefined) => void;
  occupyAdyenWalletSlot: (walletType: AdyenWalletType) => void;
  clearAdyenWalletSlot: (walletType: AdyenWalletType) => void;
  // Drops every local wallet-slot marker (logout / account switch).
  clearAllAdyenWalletSlots: () => void;
  setExternalPaymentFlowLoading: (loading: boolean) => void;
  setExternalPaymentFlowCompleted: (completed: boolean) => void;
  setExternalPaymentSessionId: (sessionId: string | null) => void;
};

export const isAdyenWalletSlotLocallyOccupied = (
  occupiedAt: Partial<Record<AdyenWalletType, number>> | undefined,
  walletType: AdyenWalletType
): boolean => occupiedAt?.[walletType] != null;

export const usePaymentStore = create<PaymentState & PaymentActions>()(
  (set) => ({
    selectedPaymentCard: EMPTY_SELECTED_PAYMENT_CARD,
    selectedCorporateAccountPaymentOption: INITIAL_CORPORATE_ACCOUNT_OPTION,
    creditCardCreationTerminalUrl: undefined,
    adyenCardAddSession: undefined,
    adyenWalletSlotOccupiedAt: {},
    externalPaymentFlowLoading: false,
    externalPaymentFlowCompleted: false,
    externalPaymentSessionId: null,
    setSelectedPaymentCard: (card) => set({ selectedPaymentCard: card }),
    resetSelectedPaymentCard: () =>
      set({ selectedPaymentCard: EMPTY_SELECTED_PAYMENT_CARD }),
    setSelectedCorporateAccountPaymentOption: (option) =>
      set({ selectedCorporateAccountPaymentOption: option }),
    setCreditCardCreationTerminalUrl: (url) =>
      set({ creditCardCreationTerminalUrl: url }),
    setAdyenCardAddSession: (session) => set({ adyenCardAddSession: session }),
    occupyAdyenWalletSlot: (walletType) =>
      set((state) => ({
        adyenWalletSlotOccupiedAt: {
          ...state.adyenWalletSlotOccupiedAt,
          [walletType]: Date.now(),
        },
      })),
    clearAdyenWalletSlot: (walletType) =>
      set((state) => {
        const next = { ...state.adyenWalletSlotOccupiedAt };
        delete next[walletType];
        return { adyenWalletSlotOccupiedAt: next };
      }),
    clearAllAdyenWalletSlots: () => set({ adyenWalletSlotOccupiedAt: {} }),
    setExternalPaymentFlowLoading: (loading) =>
      set({ externalPaymentFlowLoading: loading }),
    setExternalPaymentFlowCompleted: (completed) =>
      set({ externalPaymentFlowCompleted: completed }),
    setExternalPaymentSessionId: (sessionId) =>
      set({ externalPaymentSessionId: sessionId }),
  })
);
